import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';

import {
  getContractStagesSettings,
  deleteContractStagesSettings,
  saveContractStageSettings,
} from '../../../../../packages/common/api';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import ReportDeleteIcon from '../../../../../packages/common/shared-ui/src/icons/ReportDeleteIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UserNewEditIcon from '../../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

// Original class: rowButton
const StyledRowButton = styled(IconButton)(theme => ({
  '&.MuiButtonBase-root.MuiIconButton-root': {
    borderLeft: '1px solid #E4E4EF',
    height: '100%',
    borderRadius: 0,
    minWidth: '32px',
  },

  '& .MuiSvgIcon-root': {
    height: 'auto',
  },
}));
// Original class: rowItem
const StyledRowItem = styled(Grid)(theme => ({
  borderTop: '1px solid #E4E4EF',
}));
// Original class: rowCell
const StyledRowCell = styled(Grid)(theme => ({
  padding: '10px',
  fontSize: '14px',
  flex: 1,

  '& + .MuiGrid-root': {
    borderLeft: '1px solid #E4E4EF',
  },

  '& .MuiFormControl-root': {
    height: '100%',
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel, {
  shouldForwardProp: prop => !['emptyStyle', 'disabledStyle'].includes(prop),
})(({ theme, emptyStyle, disabledStyle }) => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC !important',
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '-3px',
    transform: 'none',
    left: '10px',
    top: '15px',
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
      ...(emptyStyle && {
        color: '#7174AC !important',
      }),
      ...(disabledStyle && {
        color: '#9e9e9e !important',
      }),
    },
    ...(disabledStyle && {
      color: '#DCDFF4 !important',
    }),
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select)(theme => ({
  '&.MuiInputBase-root.MuiInput-root': {
    marginTop: 0,
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '10px',
    // color: '#7174AC',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
  },
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346 !important',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
// Original class: menuItem
const StyledMenuItem = styled(MenuItem)(theme => ({
  '&.Mui-selected': {
    background: '#DCDFF4 !important',
  },

  '& .MuiTypography-root': {
    textTransform: 'unset',
    color: '#212346',
  },
}));
// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: rowHeader
const StyledRowHeader = styled(Grid)(theme => ({
  padding: '18px 10px',
  fontWeight: 'bold',
  flex: 1,

  '& + .MuiGrid-root': {
    borderLeft: '1px solid #E4E4EF',
  },
}));
const ContractStagesTable = ({ setHasCustomerRecords, setErrorMessage }) => {
  const getLocalString = useTranslation();
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [initialContracts, setInitialContracts] = useState([]);
  const [hasNewRow, setHasNewRow] = useState(false);

  const checkIfAnyContractIsChecked = useCallback(
    data => {
      const hasCheckedContract = data.some(contract => contract.isChecked);
      setHasCustomerRecords(hasCheckedContract);
    },
    [setHasCustomerRecords],
  );

  useEffect(() => {
    let ignore = false;

    const fetchContractsData = async () => {
      try {
        setLoading(true);
        const response = await getContractStagesSettings();

        if (!ignore) {
          setContracts(response.data);
          setInitialContracts(response.data);
          checkIfAnyContractIsChecked(response.data);
        }

        setLoading(false);
      } catch (e) {
        console.error(e);
      }
    };

    fetchContractsData();

    return () => {
      ignore = true;
    };
  }, []);
  const checkRowValue = useCallback(item => {
    const isValidCustomerId = item.customerId;

    const checkedStages = item.stage.filter(stage => stage.isChecked);

    const isValidCheckedStage =
      checkedStages.length === 1 && checkedStages[0].valueId;

    const customerIdInt = isValidCustomerId ? Number(item.customerId) : null;
    const checkedStageValueIdInt = isValidCheckedStage
      ? Number(checkedStages[0].valueId)
      : null;

    return {
      isValid: isValidCustomerId && isValidCheckedStage,
      customerId: customerIdInt,
      stageValueId: checkedStageValueIdInt,
    };
  }, []);

  const addObjectHandler = useCallback(() => {
    setContracts([
      ...contracts,
      {
        id: `newRow-${contracts.length + 1}`,
        new: true,
        isChecked: true,
        customerId: '',
        customerName: '',
        stage: [],
      },
    ]);
    setHasNewRow(true);
  }, [contracts]);

  const selectHandler = useCallback(
    (event, id) => {
      const updatedContracts = contracts.map(item => {
        if (item.id === id) {
          const sourceContract = contracts.filter(
            contract => contract.customerId === event.target.value,
          );
          return {
            ...item,
            customerName: sourceContract[0].customerName,
            stage: sourceContract[0].stage,
            customerId: event.target.value,
          };
        }
        return item;
      });
      setContracts(updatedContracts);
    },
    [contracts],
  );
  const selectStageHandler = useCallback(
    (event, id) => {
      const updatedContracts = contracts.map(contract => {
        if (contract.id === id) {
          const newStages = contract.stage.map(stage => ({
            ...stage,
            isChecked: stage.valueId === event.target.value,
          }));
          return {
            ...contract,
            stage: newStages,
          };
        }
        return contract;
      });
      setContracts(updatedContracts);
    },
    [contracts],
  );

  const toggleEdit = useCallback(
    item => {
      const updatedContracts = contracts.map(contract => {
        if (contract.id === item.id) {
          return {
            ...contract,
            edit: true,
          };
        }
        return contract;
      });
      setContracts(updatedContracts);
    },
    [contracts],
  );
  const resetItem = useCallback(
    itemId => {
      const initialItem = initialContracts.find(
        contract => contract.id === itemId,
      );
      const updatedContracts = contracts.map(contract => {
        if (contract.id === itemId) {
          return {
            ...initialItem,
          };
        }
        return contract;
      });
      setContracts(updatedContracts);
    },
    [contracts, initialContracts],
  );

  const checkError = useCallback(
    (error, item, errorSource = 'default') => {
      if (
        error.response.data.message ===
        'The customer stage connot be removed since it is used within contract schedules'
      ) {
        const initialItem = initialContracts.find(
          contract => contract.id === item.id,
        );
        const errorContractStage = initialItem.stage.find(
          stage => stage.isChecked,
        ).valueName;
        setErrorMessage(
          `${getLocalString('AdminContractStages', 'errors', 'errorPartOne')} "${errorContractStage}" ${getLocalString(
            'AdminContractStages',
            'errors',
            'errorPartTwo',
          )} ${
            errorSource === 'edit'
              ? `${getLocalString('AdminContractStages', 'errors', 'errorEdit')}`
              : ''
          } ${errorSource === 'delete' ? `${getLocalString('AdminContractStages', 'errors', 'errorDelete')}` : ''}`,
        );
      } else {
        setErrorMessage(error.response.data.message);
      }
    },
    [getLocalString, initialContracts, setErrorMessage],
  );

  const saveEditHandler = useCallback(
    async item => {
      const checkedValue = checkRowValue(item);
      if (!checkedValue.isValid) {
        setErrorMessage(getLocalString('errors', 'emptyFields'));
        return;
      }
      const data = {
        customerId: checkedValue.customerId,
        stageFieldId: checkedValue.stageValueId,
      };
      let newRow;
      if (hasNewRow) {
        newRow = contracts.find(contract => contract.new);
      }
      if (item.edit) {
        const initialItem = initialContracts.find(
          contract => contract.id === item.id,
        );
        if (Number(initialItem.customerId) !== data.customerId) {
          try {
            await deleteContractStagesSettings(item.id);
          } catch (error) {
            console.error('ERROR WITH DELETE', error);
            checkError(error, item, 'edit');
            resetItem(item.id);
            return;
          }
        }
      }
      try {
        const response = await saveContractStageSettings(data);
        if (response) {
          setInitialContracts(response.data);
          if (item.edit && newRow && Array.isArray(response.data)) {
            response.data.push(newRow);
          }
          setContracts(response.data);
          if (item.new) {
            setHasNewRow(false);
          }
          checkIfAnyContractIsChecked(contracts);
        }
      } catch (error) {
        resetItem(item.id);
        checkError(error, item, 'edit');
        console.error('ERROR WITH SAVE', error);
      }
    },

    [
      checkError,
      checkIfAnyContractIsChecked,
      checkRowValue,
      contracts,
      hasNewRow,
      resetItem,
      setErrorMessage,
    ],
  );
  const deleteHandler = useCallback(
    async item => {
      if (item.new) {
        const newContracts = contracts.filter(
          contract => contract.id !== item.id,
        );
        setContracts(newContracts);
        setHasNewRow(false);
        return;
      }
      try {
        const response = await deleteContractStagesSettings(item.id);
        setInitialContracts(response.data);
        if (response) {
          const newContracts = contracts.filter(
            contract => contract.id !== item.id,
          );
          setContracts(newContracts);
          setHasNewRow(false);
          checkIfAnyContractIsChecked(contracts);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
        checkError(error, item, 'delete');
        resetItem(item.id);
      }
    },
    [checkError, checkIfAnyContractIsChecked, contracts, resetItem],
  );
  const buttonsMap = new Map([
    [
      'edit',
      {
        handler: toggleEdit,
        icon: <UserNewEditIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'submit',
      {
        handler: saveEditHandler,
        icon: <SubmitIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'delete',
      {
        handler: deleteHandler,
        icon: <ReportDeleteIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
  ]);

  const RowButton = ({ children, action, rowIndex, item }) => {
    children = buttonsMap.get(action).icon;
    return (
      <StyledRowButton onClick={() => buttonsMap.get(action).handler(item)}>
        {children}
      </StyledRowButton>
    );
  };

  const RowComponent = ({ item, rowIndex, type }) => {
    let buttons = ['edit', 'delete'];
    item.new && (buttons = ['submit', 'delete']);
    item.edit && (buttons = ['submit', 'delete']);
    return (
      <StyledRowItem container>
        <StyledRowCell
          item
          sx={{ minWidth: '490px', p: item.new || item.edit ? 0 : '10px' }}
        >
          {item.new || item.edit ? (
            <FormControl sx={{ width: '100%' }}>
              <StyledSelectLabel
                emptyStyle={item.customerId === ''}
                id='select-label1'
              >
                {getLocalString('labels', 'select')}
              </StyledSelectLabel>
              <StyledRowSelect
                native={false}
                labelId='select-label1'
                label={getLocalString('labels', 'select')}
                onChange={event => selectHandler(event, item.id)}
                variant='standard'
                sx={{ width: '100%', border: 'none' }}
                value={item.id}
                defaultValue='defaultValue'
                MenuProps={{
                  slots: { paper: StyledPaperDropdown },
                }}
                IconComponent={props => (
                  <ChevronIcon direction={'down'} {...props} />
                )}
                renderValue={value => {
                  return item.customerId !== '' ? item.customerName : null;
                }}
              >
                {contracts
                  .filter(
                    contract =>
                      contract.isChecked === false ||
                      contract.customerId === item.customerId,
                  )
                  .sort((a, b) => b.isChecked - a.isChecked)
                  .map((item, index) => (
                    <StyledMenuItem
                      key={item.customerId}
                      value={item.customerId}
                    >
                      {item.customerName}
                    </StyledMenuItem>
                  ))}
              </StyledRowSelect>
            </FormControl>
          ) : (
            item.customerName
          )}
        </StyledRowCell>
        <StyledRowCell
          item
          sx={{
            p: 0,
            flexShrink: 0,
            flexBasis: '400px',
          }}
        >
          <Grid container>
            <Grid sx={{ flex: 1, p: item.new || item.edit ? 0 : '10px' }}>
              {item.new || item.edit ? (
                <FormControl sx={{ width: '100%' }}>
                  <StyledSelectLabel
                    disabledStyle={item.customerId === ''}
                    id='select-label'
                  >
                    {getLocalString('labels', 'select')}
                  </StyledSelectLabel>
                  <StyledRowSelect
                    native={false}
                    labelId='select-label'
                    label={getLocalString('labels', 'select')}
                    onChange={event => selectStageHandler(event, item.id)}
                    variant='standard'
                    disabled={item.customerId === ''}
                    sx={{ width: '100%', border: 'none' }}
                    value={
                      item.stage.find(stage => stage.isChecked === true)
                        ?.valueId || ''
                    }
                    defaultValue='defaultValue'
                    MenuProps={{
                      slots: { paper: StyledPaperDropdown },
                    }}
                    IconComponent={props => (
                      <ChevronIcon direction={'down'} {...props} />
                    )}
                    renderValue={value => {
                      const renderOption = item.stage.find(
                        contract => contract.isChecked === true,
                      );
                      return renderOption
                        ? renderOption.valueName
                        : getLocalString('labels', 'select');
                    }}
                  >
                    {item.stage
                      .sort((a, b) => b.isChecked - a.isChecked)
                      .map((item, index) => (
                        <StyledMenuItem key={item.valueId} value={item.valueId}>
                          {item.valueName}
                        </StyledMenuItem>
                      ))}
                  </StyledRowSelect>
                </FormControl>
              ) : (
                item?.stage?.find(stage => stage.isChecked === true)?.valueName
              )}
            </Grid>
            <Grid>
              {buttons.map((action, index) => (
                <RowButton
                  key={index}
                  action={action}
                  rowIndex={rowIndex}
                  item={item}
                ></RowButton>
              ))}
            </Grid>
          </Grid>
        </StyledRowCell>
      </StyledRowItem>
    );
  };

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <>
          <Grid container>
            <Grid sx={{ maxWidth: '987px', flex: 1 }}>
              <StyledBlockWrapper
                sx={{
                  p: 0,
                }}
              >
                <Grid container>
                  <StyledRowHeader item sx={{ minWidth: '490px' }}>
                    <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                      {getLocalString(
                        'AdminContractStages',
                        'labels',
                        'customerTable',
                      )}
                    </Typography>
                  </StyledRowHeader>
                  <StyledRowHeader
                    item
                    sx={{ flexShrink: 0, flexBasis: '400px' }}
                  >
                    <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                      {getLocalString(
                        'AdminContractStages',
                        'labels',
                        'fieldTable',
                      )}
                    </Typography>
                  </StyledRowHeader>
                </Grid>
                {contracts
                  .filter(contract => contract.isChecked === true)
                  .map((contract, index) => {
                    return (
                      <RowComponent
                        item={contract}
                        key={index}
                        rowIndex={index}
                      ></RowComponent>
                    );
                  })}
                <Box
                  sx={{
                    p: '8px',
                    borderTop: '1px solid #E4E4EF',
                  }}
                >
                  <IconButton
                    disabled={hasNewRow}
                    onClick={addObjectHandler}
                    p={0}
                  >
                    <PlusIcon
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      color={hasNewRow ? '#E4E4EF' : '#7174AC'}
                    />
                  </IconButton>
                </Box>
              </StyledBlockWrapper>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default ContractStagesTable;
